'use client';

import { useTranslations } from 'next-intl';
import { Skeleton } from '../../../components/skeleton';
import { Tooltip } from '../../../components/tooltip';
import { BodyDefault } from '../../../components/typography';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsRow } from '../../settings/SettingsRow';
import { SettingsSection } from '../../settings/SettingsSection';
import { InternalNetworkCopyableTruncatedMonoValue } from './InternalNetworkCopyableTruncatedMonoValue';
import {
  formatOutboundEndpoint,
  type InternalNetworkOutboundEndpoint,
  type InternalNetworkSensorReleaseDisplay,
} from './internal-connection.model';

export type InternalNetworkOutboundConnectivitySectionProps = {
  outboundEndpoints: InternalNetworkOutboundEndpoint[];
  sensorRelease: InternalNetworkSensorReleaseDisplay | null;
  isSensorReleaseLoading?: boolean;
};

export function InternalNetworkOutboundConnectivitySection({
  outboundEndpoints,
  sensorRelease,
  isSensorReleaseLoading,
}: InternalNetworkOutboundConnectivitySectionProps) {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.deployment',
  );
  const tCommon = useTranslations('designSystem.common');

  return (
    <SettingsSection title={t('title')} description={t('description')}>
      <SettingsCard>
        {isSensorReleaseLoading ? <Skeleton h="40px" w="full" /> : null}
        {sensorRelease && !isSensorReleaseLoading ? (
          <>
            <SettingsRow
              label={t('sensorReleaseVersion')}
              value={sensorRelease.version}
            />
            <SettingsRow
              label={t('sensorReleaseImage')}
              value={sensorRelease.imageReference || tCommon('na')}
            />
            {sensorRelease.imageDigest ? (
              <SettingsRow
                label={t('sensorReleaseDigest')}
                value={sensorRelease.imageDigest}
              />
            ) : null}
          </>
        ) : null}
        {outboundEndpoints.map((endpoint) => {
          const formatted = formatOutboundEndpoint(endpoint);

          return (
            <SettingsRow
              key={endpoint.id}
              label={
                <Tooltip
                  title={endpoint.description}
                  positioning={{ strategy: 'fixed' }}
                >
                  <BodyDefault
                    color="fg.neutral.muted"
                    truncate
                    maxW={{ base: 'full', md: '320px' }}
                  >
                    {endpoint.description}
                  </BodyDefault>
                </Tooltip>
              }
              control={
                <InternalNetworkCopyableTruncatedMonoValue
                  displayValue={formatted.display}
                  copyValue={formatted.copyValue}
                  copyAriaLabel={tCommon('copy')}
                />
              }
            />
          );
        })}
      </SettingsCard>
    </SettingsSection>
  );
}
